"use client";

import React, { useState, useEffect } from "react";
import { Terminal, Minimize2, X, Square, ChevronRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function TerminalWindow({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <div data-section-id="59829" ref={ref} className={`
        w-full max-w-3xl mx-auto rounded-lg overflow-hidden border border-border 
        bg-background shadow-md transition-all duration-700
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
      `}>
      {}
      <div className="bg-muted/30 border-b border-border px-4 py-2 flex items-center justify-between select-none">
        <div className="flex items-center gap-2">
          <div className="size-3 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors" />
          <div className="size-3 rounded-full bg-yellow-500/80 hover:bg-yellow-500 transition-colors" />
          <div className="size-3 rounded-full bg-green-500/80 hover:bg-green-500 transition-colors" />
        </div>
        <div className="text-xs font-mono text-muted-foreground flex items-center gap-2">
          <Terminal className="size-3" />
          <span>system@robotaxi:~/intresseanmalan</span>
        </div>
        <div className="flex items-center gap-2 opacity-50">
          <Minimize2 className="size-3" />
          <Square className="size-3" />
          <X className="size-3" />
        </div>
      </div>

      {}
      <div className="p-6 font-sans text-sm md:text-base text-foreground bg-background min-h-[400px]">
        {children}
      </div>
    </div>;
}
function TerminalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [cursorVisible, setCursorVisible] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => setCursorVisible(v => !v), 530);
    return () => clearInterval(interval);
  }, []);
  if (isSubmitted && !submitError) {
    return <div className="space-y-4 animate-in fade-in duration-500">
        <div className="flex items-center gap-2 text-primary">
          <CheckCircle2 className="size-5" />
          <span className="font-serif font-bold">{successMessage}</span>
        </div>
        <div className="pl-7 text-muted-foreground font-mono text-xs">
          <p>{">"} Session established successfully.</p>
          <p>{">"} Registration ID: #{Math.floor(Math.random() * 999999)}</p>
          <p>{">"} We will contact you shortly.</p>
        </div>
        <Button onClick={resetForm} variant="ghost" className="mt-4 text-primary hover:text-primary hover:bg-primary/5 font-sans h-auto p-0 hover:underline">
          [SKICKA EN TILL ANMÄLAN]
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1 text-muted-foreground mb-6 text-xs md:text-sm font-mono">
        <p>Robotaxi Network OS v1.0.4 [Secure Connection]</p>
        <p>(c) 2025 Robotaxi Systems. Stronger Together.</p>
        <p className="text-primary/70">Initierar registreringsprotokoll...</p>
      </div>

      {}
      <FormField control={control} name="name" rules={{
      required: "Namn är obligatoriskt"
    }} render={({
      field
    }) => <FormItem className="space-y-0">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2">
              <FormLabel className="text-primary whitespace-nowrap shrink-0 font-mono text-xs">
                <span className="text-muted-foreground select-none mr-2">user@robotaxi:~$</span>
                registrera --namn
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ditt fullständiga namn" className="font-sans bg-transparent border-0 border-b border-muted focus-visible:border-primary rounded-none px-0 h-auto py-0 focus-visible:ring-0 shadow-none placeholder:text-muted-foreground/30 text-foreground caret-primary min-w-[200px] transition-colors" />
              </FormControl>
            </div>
            <FormMessage className="text-destructive text-xs pl-0 md:pl-[180px]" />
          </FormItem>} />

      {}
      <FormField control={control} name="email" rules={{
      required: "E-postadress krävs",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ogiltigt e-postformat"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-0">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2">
              <FormLabel className="text-primary whitespace-nowrap shrink-0 font-mono text-xs">
                <span className="text-muted-foreground select-none mr-2">user@robotaxi:~$</span>
                registrera --epost
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="namn@exempel.se" className="font-sans bg-transparent border-0 border-b border-muted focus-visible:border-primary rounded-none px-0 h-auto py-0 focus-visible:ring-0 shadow-none placeholder:text-muted-foreground/30 text-foreground caret-primary min-w-[200px] transition-colors" />
              </FormControl>
            </div>
            <FormMessage className="text-destructive text-xs pl-0 md:pl-[180px]" />
          </FormItem>} />

      {}
      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="space-y-0">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2">
              <FormLabel className="text-primary whitespace-nowrap shrink-0 font-mono text-xs">
                <span className="text-muted-foreground select-none mr-2">user@robotaxi:~$</span>
                registrera --ort
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Stad eller region" className="font-sans bg-transparent border-0 border-b border-muted focus-visible:border-primary rounded-none px-0 h-auto py-0 focus-visible:ring-0 shadow-none placeholder:text-muted-foreground/30 text-foreground caret-primary min-w-[200px] transition-colors" />
              </FormControl>
            </div>
            <FormMessage className="text-destructive text-xs pl-0 md:pl-[180px]" />
          </FormItem>} />

      {}
      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="space-y-0">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2">
              <FormLabel className="text-primary whitespace-nowrap shrink-0 font-mono text-xs">
                <span className="text-muted-foreground select-none mr-2">user@robotaxi:~$</span>
                registrera --foretag
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Företagsnamn (valfritt)" className="font-sans bg-transparent border-0 border-b border-muted focus-visible:border-primary rounded-none px-0 h-auto py-0 focus-visible:ring-0 shadow-none placeholder:text-muted-foreground/30 text-foreground caret-primary min-w-[200px] transition-colors" />
              </FormControl>
            </div>
            <FormMessage className="text-destructive text-xs pl-0 md:pl-[180px]" />
          </FormItem>} />

      {}
      <FormField control={control} name="message" rules={{
      required: "Meddelande saknas"
    }} render={({
      field
    }) => <FormItem className="space-y-2 pt-2">
            <FormLabel className="text-primary font-mono text-xs block">
              <span className="text-muted-foreground select-none mr-2">user@robotaxi:~$</span>
              skriv --meddelande
            </FormLabel>
            <FormControl>
              <div className="relative">
                <textarea {...field} placeholder="Berätta kort om ditt intresse för att bli operatör..." className="w-full min-h-[120px] font-sans bg-transparent border-l-2 border-muted focus:border-primary pl-4 py-2 text-foreground placeholder:text-muted-foreground/30 outline-none resize-none text-sm md:text-base transition-colors" />
              </div>
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      {}
      {submitError && <div className="flex items-center gap-2 text-destructive bg-destructive/5 p-3 rounded border border-destructive/20">
          <AlertCircle className="size-4" />
          <span className="text-xs font-mono">{submitError}</span>
        </div>}

      {}
      <div className="pt-4 flex items-center gap-2">
        <span className="text-muted-foreground select-none text-sm font-mono">{">"}</span>
        <Button type="submit" disabled={isSubmitting} variant="default" className="font-sans bg-primary text-primary-foreground hover:bg-primary/90 h-auto py-3 px-8 rounded-[3px] shadow-sm transition-all text-sm uppercase tracking-wider font-bold">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-4" />
              SKICKAR...
            </> : <>
              SKICKA INTRESSEANMÄLAN
              <span className={`inline-block w-2.5 h-4 bg-primary-foreground ml-2 align-middle ${cursorVisible ? 'opacity-100' : 'opacity-0'}`} />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function Terminalkontakt() {
  return <section id="anmäl-ditt-intresse" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000005_1px,transparent_1px),linear-gradient(to_bottom,#00000005_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
      
      <div className="relative z-10 container mx-auto">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary font-serif">
            Stronger Together
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-sans text-sm md:text-base">
            Bli en del av framtidens mobilitet. Initiera din ansökan för att bli en certifierad operatör i vårt växande nätverk.
          </p>
        </div>

        <TerminalWindow>
          <FormProvider formKey="interest_form_teslafrskringserbjudanden" formId="interest_form_teslafrskringserbjudanden" sectionName="terminal-contact" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          message: {
            required: true
          }
        }} submitText="SKICKA INTRESSEANMÄLAN" successMessage="Intresseanmälan mottagen. Protokoll slutfört." errorMessage="Kunde inte skicka. Kontrollera syntax och försök igen.">
            <TerminalForm />
          </FormProvider>
        </TerminalWindow>
      </div>
    </section>;
}